import AssetTable from "./AssetTable";
import AddAssetsPanel from "./AddAssetsPanel";
import { deriveHeaderButtons } from "./watchlistFormat";

function HeaderButton({ label, variant, onClick, disabled, title }) {
  const base = "rounded-lg px-4 py-2 text-sm font-medium transition-colors";
  const cls = disabled
    ? "border border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed"
    : variant === "filled"
    ? "bg-primaryColor text-white hover:bg-primaryColor/90"
    : "border border-primaryColor text-primaryColor bg-white hover:bg-primaryColor/5";
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-disabled={disabled || undefined}
      title={title}
      className={`${base} ${cls}`}
    >
      {label}
    </button>
  );
}

export default function MainCard({
  selectedList,
  isCurated,
  isActive,
  count,
  mode,
  items,
  variant,
  initialLoading,
  loadingMore,
  hasMore,
  onLoadMore,
  onRemove,
  onReorder,
  onEnterEdit,
  onExitEdit,
  onEnterAdd,
  onExitAdd,
  onAddAsset,
  onUseInTrade,
  useInTradeBlocked = false,
  useInTradeBlockedMessage = "",
}) {
  const cardClass =
    "w-full max-w-3xl rounded-2xl border border-gray-200 bg-white p-6 md:p-8";

  if (!selectedList) {
    return (
      <div className={cardClass}>
        <p className="py-16 text-center text-sm text-secondary">Select a list to view its assets</p>
      </div>
    );
  }

  if (mode === "add") {
    return (
      <div className={cardClass}>
        <AddAssetsPanel listName={selectedList.name} onClose={onExitAdd} onAdd={onAddAsset} />
      </div>
    );
  }

  const buttons = deriveHeaderButtons({ isCurated, isActive, mode });
  const editMode = mode === "edit";

  return (
    <div id="watchlist-guide-assets" className={cardClass}>
      {/* Header */}
      <div id="watchlist-guide-header" className="mb-5 flex items-start justify-between gap-4">
        <div className="min-w-0">
          <h2 className="truncate text-2xl font-bold text-[#004d4d] md:text-3xl">
            {selectedList.name}
          </h2>
          <div className="mt-1 flex items-center gap-1.5 text-sm text-secondary">
            <span>{count} assets</span>
            <span>·</span>
            {isCurated ? (
              <span>Curated by QUSHi</span>
            ) : editMode ? (
              <button
                type="button"
                onClick={onExitEdit}
                className="font-medium"
                style={{ color: "#006565" }}
              >
                Done
              </button>
            ) : (
              <button
                type="button"
                onClick={onEnterEdit}
                className="font-medium"
                style={{ color: "#006565" }}
              >
                Edit
              </button>
            )}
          </div>
        </div>

        {buttons.length > 0 && (
          <div className="flex shrink-0 gap-2">
            {buttons.map((b) => {
              // Client Rev 2: "If a watchlist has zero eligible assets, disable
              // Use in Trade… Do not mark that list Active. Do not change the
              // current Trade wheel." Only Use in Trade is affected — Add Assets
              // still works, because the list itself is not the problem.
              const blocked = b.key === "useInTrade" && useInTradeBlocked;
              return (
                <div
                  key={b.key}
                  id={b.key === "useInTrade" ? "watchlist-guide-use-in-trade" : undefined}
                >
                  <HeaderButton
                    label={b.label}
                    variant={b.variant}
                    disabled={blocked}
                    title={blocked ? useInTradeBlockedMessage : undefined}
                    onClick={b.key === "addAssets" ? onEnterAdd : onUseInTrade}
                  />
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Client Rev 2's exact wording, shown rather than only offered as a
          tooltip: a greyed-out button with no reason beside it is the "dead
          button" complaint this screen has already had once. */}
      {useInTradeBlocked && (
        <p
          id="watchlist-use-in-trade-blocked"
          className="-mt-2 mb-4 text-sm text-secondary"
        >
          {useInTradeBlockedMessage}
        </p>
      )}

      <div className="max-h-[calc(100vh-20rem)] min-h-[16rem] overflow-y-auto">
        <AssetTable
          items={items}
          variant={variant}
          editMode={editMode}
          onRemove={onRemove}
          onReorder={onReorder}
          initialLoading={initialLoading}
          loadingMore={loadingMore}
          hasMore={hasMore}
          onLoadMore={onLoadMore}
        />
      </div>
    </div>
  );
}
